import { CSSProperties, useEffect } from 'react';
import { type DashboardTarget } from '../../../lib/dashboard';
import { DashboardErrorBanner } from '../../DashboardErrorBanner';
import { DashboardSkeleton } from './DashboardSkeleton';
import { CONTENT_HEIGHT_SAFETY_PX } from './constants';
import { useDashboardFrame } from './use-dashboard-frame';

export function DashboardFrame({
  target,
  name,
  workspace,
  reloadKey,
  docHeight,
  onHeight,
  onReady,
  onRetry,
}: {
  target: DashboardTarget;
  name: string;
  workspace?: string;
  reloadKey: number;
  docHeight: number | null;
  onHeight: (height: number) => void;
  onReady?: () => void;
  // Re-fetch this dashboard (a reloadKey bump). Used by the outage notice's retry and after a card's write.
  onRetry?: () => void;
}) {
  const { iframeRef, src, basePath, errors, contentReady, onIframeLoad, cardActionElement } = useDashboardFrame({
    target,
    reloadKey,
    workspace,
    docHeight,
    onHeight,
    onReload: onRetry,
  });

  useEffect(() => {
    if (contentReady) onReady?.();
  }, [contentReady, onReady]);

  return (
    <div style={{ ...styles.root, height: docHeight ? 'auto' : '100%' }}>
      {errors.length > 0 && (
        <DashboardErrorBanner
          basePath={basePath}
          title={name}
          errors={errors}
          onRetry={onRetry}
          palette={{ color: 'rgba(var(--ds-fill-destructive-active-rgb), 0.95)', background: 'rgba(var(--ds-text-pending-rgb), 0.12)', border: 'rgba(var(--ds-text-pending-rgb), 0.3)' }}
        />
      )}
      <div style={{ ...styles.frameBox, flex: docHeight != null ? '0 0 auto' : '1 1 auto' }}>
        <iframe
          ref={iframeRef}
          title={name}
          sandbox="allow-scripts"
          src={src}
          onLoad={onIframeLoad}
          style={{ ...styles.iframe, height: docHeight ? `${docHeight + CONTENT_HEIGHT_SAFETY_PX}px` : '100%' }}
        />
        <div
          aria-hidden
          style={{
            ...styles.cover,
            opacity: contentReady ? 0 : 1,
            transition: contentReady ? 'opacity 260ms ease' : 'none',
          }}
        >
          <DashboardSkeleton />
        </div>
      </div>
      {cardActionElement}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  root: { position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' },
  frameBox: { position: 'relative', width: '100%', minHeight: 0 },
  iframe: { display: 'block', width: '100%', border: 'none', background: 'transparent' },
  cover: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'var(--field-color)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    pointerEvents: 'none',
  },
};
